import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { pathOr } from 'ramda'
import Select from '../../../atoms/Select/Select'
import TextField from '../../../atoms/TextField/TextField'
import Button from '../../../atoms/Button/Button'
import styles from './EftBeneficiary.module.scss'
import { partyApiSelectors } from '../../../../../modules/api/party'
import CurrencyField from '../../CurrencyField/CurrencyField'
import CurrencyText from '../../../atoms/Typography/CurrencyText/CurrencyText'
import TextInput from 'views/components/atoms/TextField/TextInput'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import PartySearchOrCreate from 'views/containers/Search/PartySearchOrCreate'
import { validatePaymentReference } from 'utils/validation'

type TPartyTag = {
  value: string
  label: string
}

type TState = {
  partyId: string
  name: string
  partyTag: string
  reference: string
  amount: number
}

interface IEftBeneficiaryProps {
  partyTags: TPartyTag[]
  state: TState
  setState: (state: TState) => void
  onNewBeneficiaryAdded: (beneficiary: any) => void
  getDefaultResultSets?: (partyTag: string) => any[]
  tags?: string
  propertyOwnerName?: string | null
  unallocatedAmount: number
}

const PARTY_TAG_LABELS: Array<[RegExp | string, string]> = [
  ['Owner', 'Owner'],
  [/^Contractor/, 'Contractor'],
  [/^Municipality/, 'Municipality'],
  [/^BodyCorporate/, 'Body Corporate'],
  [/^Supplier/, 'Supplier'],
  ['Tenant', 'Tenant'],
  ['Applicant', 'Applicant'],
]

const EftBeneficiary = ({
  partyTags,
  onNewBeneficiaryAdded,
  state,
  setState,
  getDefaultResultSets,
  tags,
  propertyOwnerName,
  unallocatedAmount,
}: IEftBeneficiaryProps) => {
  const [nextStep, setNextStep] = useState('partyTag')
  const [referenceError, setReferenceError] = useState<string | undefined>(undefined)

  /**
   * Skip straight to party search if partyTags weren't grouped.
   * EG: Municipality: Electricity, Municipality: Sewerage
   */
  useEffect(() => {
    if (partyTags?.length === 1) {
      setState({ ...state, partyTag: partyTags[0].value })
      setNextStep('partySearch')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const agencyParty = useSelector(partyApiSelectors.getAgencyParty)
  const defaultResultSets = getDefaultResultSets ? getDefaultResultSets(state.partyTag) : []

  // Skip party search if partyTag is 'Agency' and set party to agency
  useEffect(() => {
    if (state.partyTag === 'Agency' && nextStep !== 'amount') {
      if (agencyParty) {
        setState({
          ...state,
          name: pathOr('', ['companyName'], agencyParty),
          partyId: pathOr('', ['id'], agencyParty),
        })
        setNextStep('reference')
      }
    }
  }, [state.partyTag, agencyParty, nextStep])

  const renderNextField = () => {
    if (nextStep === 'partyTag') {
      return (
        <Select
          defaultMenuIsOpen={true}
          onChange={({ value }: any) => {
            setState({ ...state, partyTag: value })
            setNextStep('partySearch')
          }}
          name="partyTag"
          label="Beneficiary tag"
          labelHint="This will appear on the lease statement"
          placeholder="Select"
          options={partyTags}
        />
      )
    }

    const partySelectLabel =
      PARTY_TAG_LABELS.find(([m]) => (typeof m === 'string' ? state.partyTag === m : m.test(state.partyTag)))?.[1] ??
      'Contact'

    if (nextStep === 'partySearch') {
      return (
        <PartySearchOrCreate
          name="beneficiary"
          label={`Select ${partySelectLabel}`}
          placeholder="Search Contacts"
          value={
            state.partyTag === 'Owner'
              ? defaultResultSets[0]?.length === 1
                ? propertyOwnerName ?? ''
                : ''
              : state.name
          }
          onSelect={(data: any) => {
            setState({ ...state, partyId: data.id, name: data.text })
            setNextStep('reference')
          }}
          defaultResultSets={[]}
          tags={tags}
          focusOnMount={true}
          showAddNewContact={true}
          autoSelectNewContact={true}
        />
      )
    }

    if (nextStep === 'reference') {
      return (
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="reference"
              onChange={({ target }: any) => {
                setState({ ...state, reference: target.value })
                const error = validatePaymentReference(target.value)
                setReferenceError(error)
              }}
              placeholder=""
              ref={(ref: any) => {
                ref?.focus()
              }}
            />
          }
          label={state.partyTag === 'Agency' ? 'Payment reference' : 'Statement reference'}
          labelHint={state.partyTag !== 'Agency' ? "This will appear on the beneficiary's bank statement" : ''}
          error={referenceError}
        />
      )
    }

    if (nextStep === 'amount') {
      return (
        <CurrencyField
          name="amount"
          placeholder=""
          label="Amount (optional)"
          labelHint="Leave blank to set the amount later"
          value={state.amount}
          onChange={({ target }: any) => {
            setState({ ...state, amount: target.value || 0 })
          }}
          error={
            state.amount > unallocatedAmount ? (
              <span>
                Can&apos;t exceed unallocated amount of <CurrencyText>{unallocatedAmount}</CurrencyText>
              </span>
            ) : (
              false
            )
          }
        />
      )
    }
  }

  const isStateValid = () => state.partyId && state.partyTag && state.reference && !referenceError

  return (
    <div className={styles.root}>
      <div className={styles.input}>{renderNextField()}</div>

      {nextStep === 'reference' && (
        <Button
          className={styles['next-button']}
          disabled={!state.reference || !!referenceError}
          size="sm"
          onClick={() => setNextStep('amount')}
        >
          Next
        </Button>
      )}

      {nextStep === 'amount' && (
        <Button
          className={styles['add-beneficiary-button']}
          disabled={!isStateValid()}
          size="sm"
          onClick={() => onNewBeneficiaryAdded({ type: 'PartyBeneficiary', value: state })}
        >
          Save
        </Button>
      )}
    </div>
  )
}

export default EftBeneficiary
